'use client';

import Image from 'next/image';
import { Star, Loader2 } from 'lucide-react';
import { cn } from '@/shared/lib/cn';
import type { RenderPickerItem } from '../model/types';

interface RenderGridItemProps {
  item: RenderPickerItem;
  isDownloading: boolean;
  onClick: () => void;
}

/**
 * Grid item card for render picker
 * Shows render image with badges for upscaled/favorite status
 */
export function RenderGridItem({
  item,
  isDownloading,
  onClick,
}: RenderGridItemProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={isDownloading}
      className={cn(
        'relative aspect-[4/3] rounded-lg overflow-hidden group',
        'ring-0 hover:ring-2 hover:ring-primary',
        'transition-all duration-150',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        isDownloading && 'pointer-events-none opacity-75'
      )}
    >
      {/* Image */}
      <Image
        src={item.imageUrl}
        alt={item.conceptName || 'Render'}
        fill
        className="object-cover"
        sizes="(max-width: 640px) 45vw, (max-width: 1024px) 30vw, 20vw"
      />

      {/* Hover overlay */}
      <div
        className={cn(
          'absolute inset-0 bg-black/0 group-hover:bg-black/20',
          'transition-colors duration-200'
        )}
      />

      {/* Top-right badges — matches Apple RenderGridItemView */}
      {(item.isFavorite || item.isUpscaled) && (
        <div className="absolute top-1.5 end-1.5 flex items-center gap-1 bg-black/60 rounded px-1.5 py-1">
          {item.isFavorite && (
            <Star className="w-3 h-3 text-yellow-400 fill-yellow-400" />
          )}
          {false && /* UPSCALE_HIDDEN */ item.isUpscaled && (
            <span className="text-[10px] font-bold text-white leading-none">
              HD
            </span>
          )}
        </div>
      )}

      {/* Bottom concept name */}
      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2 pt-6">
        <p className="text-white text-card-meta truncate">
          {item.conceptName || 'Unnamed'}
        </p>
      </div>

      {/* Loading overlay */}
      {isDownloading && (
        <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
          <Loader2 className="w-8 h-8 text-white animate-spin" />
        </div>
      )}
    </button>
  );
}
